import { EmptyState, Icons, Spinner } from "construct-ui";
import NumberInputComponent from "../../../components/number/number-input.component";
import SingleSelectComponent from "../../../components/single-select/single-select.component";
import TreeComponent from "../../../components/treeview/tree.component";
import codeMasterModel from "../../../models/common/code-master.model";
import MenuHierarchyModel from "../../../models/master-data-management/menu-hierarchy/menu-hierarchy.model";
import ViewBase from "../../view-base.view";
import styles from "../../../../stylesheets/styles.scss";

/**
 * Menu Hierarchy View
 * 
 * Menu Hierarchy page for editing New MCS and MCS Mobile menus, deletion is not allowed
 * 
 * @author Darrus Goh
 */
export default function MenuHierarchyView() {
    ViewBase.call(this);

    const SYSTEM_TYPE_WEB = 'Web';
    const LIST_HEIGHT = '700px';
    const CODEMASTER_CATEGORY = 'SystemType';
    const ACTIONS = {
        ADD: 0,
        EDIT: 1
    };

    const DEFAULT_MENU_DETAILS = {
        parentMenuId: null,
        icon: null,
        menuId: '',
        name: '',
        description: '',
        path: '',
        category: '',
        sequence: '',
        active: false,
        offlineModeYN: 'N'
    };

    let selectedAction = ACTIONS.ADD;
    let isTreeLoading = false;
    let isDetailsLoading = false;

    let systemTypes = [];
    let selectedSystemType = null;

    let menuHierarchy = [];
    let parentMenuList = [];

    let menuDetailsBeforeEdit = structuredClone(DEFAULT_MENU_DETAILS);
    let menuDetails = structuredClone(DEFAULT_MENU_DETAILS);

    let icons = [];
    let iconElementMap = {};

    const getSystemTypes = () => {
        return codeMasterModel.getByCategoryV2({ category: CODEMASTER_CATEGORY })
            .then((result) => {
                systemTypes = result;
            })
            .catch((error) => {
                this.handleError(error);
            });
    };

    const getMenuHierarchy = (systemType) => {
        isTreeLoading = true;

        return MenuHierarchyModel.getMenuHierarchy(systemType)
            .then(prepareMenuHierarchyAndParentMenuList)
            .catch((error) => {
                this.handleError(error);
            })
            .finally(() => {
                isTreeLoading = false;
            });
    };

    const prepareMenuHierarchyAndParentMenuList = (result) => {
        menuHierarchy = structuredClone(result);
        parentMenuList = [];

        for (let menu of menuHierarchy) {
            recursivePrepareMenuHierarchyAndParentMenuList(menu);
        }

        parentMenuList.sort((a, b) => {
            if (a.name > b.name)
                return 1;
            else if (a.name < b.name)
                return -1;
            else
                return 0;
        });
    }

    const recursivePrepareMenuHierarchyAndParentMenuList = (menu) => {
        menu.class = !menu.active ? 'placeholder-text-color' : '';
        menu.name += ` [${menu.code}]`;
        if (menu.children.length > 0) {
            const children = menu.children;

            for (let child of children) {
                recursivePrepareMenuHierarchyAndParentMenuList(child);
            }
        }

        // Accumulate all menu for Parent Menu List
        parentMenuList.push({ code: menu.code, name: menu.name });
    }

    const getMenu = (menuId) => {
        isDetailsLoading = true;

        return MenuHierarchyModel.getMenu(menuId)
            .then((result) => {
                menuDetails = structuredClone(result);
                menuDetailsBeforeEdit = structuredClone(result);
            })
            .catch((error) => {
                this.handleError(error);
            })
            .finally(() => {
                isDetailsLoading = false;
            });
    };

    const addMenu = (details) => {
        return MenuHierarchyModel.addMenu(getRequestBody(details, selectedSystemType.code))
            .then(onSuccess)
            .catch((error) => {
                this.handleError(error);
            });
    };

    const updateMenu = (details) => {
        if (!hasChanges()) {
            this.showWarningMessage(this.getText('EM00000755', 'No changes were made.'), this.getText('LT00000737', 'No Changes'));
            return;
        }

        return MenuHierarchyModel.updateMenu(getRequestBody(details, selectedSystemType.code))
            .then(onSuccess)
            .catch((error) => {
                this.handleError(error);
            })
    };

    const onSuccess = () => {
        this.showMessage(this.getText('EM00000762', 'Changes have been saved.'), this.getText('EM00000004', 'Success'), () => {
            menuDetailsBeforeEdit = structuredClone(menuDetails);
            getMenuHierarchy(selectedSystemType.code);
        });
    }

    const getRequestBody = (details, systemType) => {
        let body = structuredClone(details);
        body.newMenuId = details.menuId;
        body.systemType = systemType;
        return body;
    };

    const getBootstrapIconDropdownOptions = () => {
        return styles.bootstrapIcons.split(',').map((icon) => {
            icon = `bi-${icon.replaceAll("\"", '').trim()}`;
            return { code: `bi ${icon}`, name: icon };
        });
    }

    const getBootstrapIconElementMap = (icons) => {
        let result = {};
        for (const icon of icons) {
            result[icon.code] = m(`i.bi.${icon.name}`);
        }
        return result;
    };

    const setAction = (action) => {
        selectedAction = action;
    }

    const resetFields = () => {
        menuDetailsBeforeEdit = structuredClone(DEFAULT_MENU_DETAILS);
        menuDetails = structuredClone(DEFAULT_MENU_DETAILS);
    }

    const leavePageEventListener = (e) => {
        if (hasChanges() && !confirm(this.getText('EM00000776', 'Changes you made may not be saved'))) {
            e.preventDefault();
            e.stopImmediatePropagation();
        }
    };

    const hasChanges = () => {
        for (const key in menuDetailsBeforeEdit) {
            if (menuDetailsBeforeEdit[key] !== menuDetails[key]) {
                return true;
            }
        }
        return false;
    }

    return {
        oninit: () => {
            icons = getBootstrapIconDropdownOptions();
            iconElementMap = getBootstrapIconElementMap(icons);
            getSystemTypes();
        },
        oncreate: () => {
            addEventListener('route', leavePageEventListener);
            addEventListener('beforeunload', leavePageEventListener);
        },
        onremove: () => {
            removeEventListener('route', leavePageEventListener);
            removeEventListener('beforeunload', leavePageEventListener);
        },
        view: () => [
            m('.d-flex.justify-content-end.align-items-center.mb-3',
                m('button.btn.btn-primary',
                    {
                        onclick: () => {
                            if (!hasChanges() || confirm(this.getText('EM00000776', 'Changes you made may not be saved'))) {
                                resetFields();
                                setAction(ACTIONS.ADD);
                            }
                        }
                    },
                    m('i.bi.bi-plus-lg'), this.getText('LT00000774', 'Add Menu'),
                ),
            ),
            m('.row.g-1', { style: { height: LIST_HEIGHT } },
                // LEFT PANE
                m('.col-4.h-100',
                    m('.card.h-100',
                        m('.card-body.h-100',
                            m('.d-flex.h-100.flex-column.gap-3',
                                m('div',
                                    m('label.form-label', this.getText('LT00000775', 'System Type')),
                                    m(SingleSelectComponent, {
                                        items: systemTypes,
                                        selectedItem: selectedSystemType,
                                        checkmark: true,
                                        filterable: false,
                                        allowEmpty: false,
                                        onSelect: (selectedItem) => {
                                            if (!hasChanges() || confirm(this.getText('EM00000776', 'Changes you made may not be saved'))) {
                                                selectedSystemType = selectedItem;
                                                getMenuHierarchy(selectedSystemType.code);
                                                resetFields();
                                            }
                                        }
                                    })
                                ),
                                m('.flex-fill.overflow-auto.position-relative',
                                    menuHierarchy && menuHierarchy.length > 0
                                        ? m(TreeComponent, {
                                            items: menuHierarchy,
                                            selectedItem: menuDetails?.code,
                                            multiple: true,
                                            globalOnClick: (selectedItem) => {
                                                if (!hasChanges() || confirm(this.getText('EM00000776', 'Changes you made may not be saved'))) {
                                                    getMenu(selectedItem);
                                                    setAction(ACTIONS.EDIT);
                                                }
                                            }
                                        })
                                        : m(EmptyState, {
                                            class: 'placeholder-text-color',
                                            icon: Icons.ARCHIVE,
                                            content: this.getText("LT00000317", "No results found"),
                                            fill: true
                                        })
                                    ,
                                    isTreeLoading && m(Spinner, { fill: true, background: true, active: true })
                                )
                            ),
                        )
                    )
                ),

                // RIGHT PANE
                m('.col-8',
                    m('.card.',
                        m('.card-body',
                            m('form', {
                                onsubmit: (e) => {
                                    e.preventDefault();
                                    e.stopImmediatePropagation();
                                    
                                    if (selectedAction === ACTIONS.ADD) {
                                        addMenu(menuDetails);
                                    } else if (selectedAction === ACTIONS.EDIT) {
                                        updateMenu(menuDetails);
                                    }
                                }
                            },
                                m('.row.g-2',
                                    m('.col-4',
                                        m('label.form-label.mandatory', this.getText('LT00000776', 'Menu ID')),
                                        m('input.form-control', {
                                            type: 'text',
                                            value: menuDetails?.menuId,
                                            disabled: selectedAction === ACTIONS.EDIT,
                                            readonly: selectedAction === ACTIONS.EDIT,
                                            maxlength: 8,
                                            required: true,
                                            onchange: (element) => {
                                                const uppercaseValue = element.target.value?.toUpperCase();
                                                if (selectedAction === ACTIONS.EDIT) {
                                                    menuDetails.newMenuId = uppercaseValue;
                                                } else {
                                                    menuDetails.menuId = uppercaseValue;
                                                }
                                            }
                                        })
                                    ),
                                    m('.col-4',
                                        m('label.form-label.mandatory', this.getText('LT00000011', 'Name')),
                                        m('input.form-control', {
                                            type: 'text',
                                            value: menuDetails?.name,
                                            maxlength: 50,
                                            required: true,
                                            onchange: (element) => {
                                                menuDetails.name = element.target.value;
                                            }
                                        })
                                    ),
                                    m('.col-4',
                                        m('label.form-label.mandatory', this.getText('LT00000512', 'Sequence')),
                                        m(NumberInputComponent, {
                                            min: 0,
                                            max: 100000,
                                            value: menuDetails?.sequence,
                                            required: true,
                                            onChange: (inputNumber) => {
                                                menuDetails.sequence = inputNumber;
                                            }
                                        })
                                    ),
                                    m('.col-4',
                                        m('label.form-label', this.getText('LT00000769', 'Parent Menu ID')),
                                        m(SingleSelectComponent, {
                                            items: parentMenuList,
                                            selectedItem: menuDetails?.parentMenuId,
                                            keyOnly: true,
                                            onSelect: (selectedItem) => {
                                                menuDetails.parentMenuId = selectedItem;
                                            }
                                        })
                                    ),
                                    m('.col-8',
                                        m('label.form-label', this.getText('LT00000770', 'Path')),
                                        m('input.form-control', {
                                            type: 'text',
                                            value: menuDetails?.path,
                                            maxlength: 100,
                                            onchange: (element) => {
                                                menuDetails.path = element.target.value;
                                            }
                                        })
                                    ),
                                    m('.col-8',
                                        m('label.form-label', this.getText('LT00000012', 'Description')),
                                        m('input.form-control', {
                                            type: 'text',
                                            value: menuDetails?.description,
                                            maxlength: 100,
                                            onchange: (element) => {
                                                menuDetails.description = element.target.value;
                                            }
                                        })
                                    ),
                                    m('.col-4',
                                        m('label.form-label', this.getText('LT00000366', 'Category')),
                                        m('input.form-control', {
                                            type: 'text',
                                            value: menuDetails?.category,
                                            maxlength: 100,
                                            onchange: (element) => {
                                                menuDetails.category = element.target.value;
                                            }
                                        })
                                    ),
                                    selectedSystemType?.code === SYSTEM_TYPE_WEB && menuDetails?.parentMenuId === '' && m('.col-4',
                                        m('label.form-label', this.getText('LT00000771', 'Icon')),
                                        m(SingleSelectComponent, {
                                            items: icons,
                                            selectedItem: menuDetails?.icon,
                                            itemIcons: iconElementMap,
                                            keyOnly: true,
                                            onSelect: (icon) => {
                                                menuDetails.icon = icon;
                                            }
                                        })
                                    ),
                                    m('.col-1',
                                        m('label.form-label', this.getText('LT00000025', 'Active')),
                                        m('div', { style: { 'padding': '10px 5px' } },
                                            m('.form-check.form-switch',
                                                m('input.form-check-input', {
                                                    type: 'checkbox',
                                                    checked: menuDetails?.active,
                                                    onchange: (e) => {
                                                        menuDetails.active = e.target.checked;
                                                    }
                                                })
                                            )
                                        )
                                    ),
                                    m('.col-2',
                                        m('label.form-label', this.getText('LT00000772', 'Offline Mode')),
                                        m('div', { style: { 'padding': '10px 5px' } },
                                            m('.form-check.form-switch',
                                                m('input.form-check-input', {
                                                    type: 'checkbox',
                                                    checked: menuDetails?.offlineModeYN === 'Y',
                                                    onchange: (e) => {
                                                        menuDetails.offlineModeYN = e.target.checked ? 'Y' : 'N';
                                                    }
                                                })
                                            )
                                        )
                                    ),
                                    m('.col-12',
                                        m('button.btn.btn-primary.float-right',
                                            {
                                                disabled: !selectedSystemType,
                                                type: 'submit'
                                            },
                                            selectedAction === ACTIONS.ADD ? this.getText('LT00000090', 'Add') : this.getText('LT00000022', 'Save')
                                        )
                                    )
                                )
                            ),
                            isDetailsLoading && m(Spinner, { active: true, fill: true, background: true })
                        )
                    )
                )
            ),
            this.getBaseViews()
        ]
    };
}